import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro, euroDezimal } from "@/utils/preisAnalyse";

/**
 * Der geburtstagsspezifische Teil.
 *
 * Aus der Search Console: "günstige fotobox mieten für kleine
 * geburtstagsfeiern" und "fotobox kindergeburtstag" — zwei Fragen, die es
 * bei einer Hochzeit nicht gibt. Genau daran hängt der Unterschied dieser
 * Seite: Eine Geburtstagsfeier ist kleiner, findet oft zu Hause statt, und
 * ein rein digitales Paket kann dort völlig ausreichen.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function GeburtstagInhalt({ analyse, digital, stand }) {
  if (!analyse) return null;

  return (
    <>
      <Abschnitt
        id="kosten"
        titel="Was kostet eine Fotobox für einen Geburtstag?"
        untertitel={`Gerechnet für ${analyse.gaeste} Gäste — die übliche Größe einer Geburtstagsfeier.`}
      >
        <p>
          Bei{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.gaeste} Gästen
          </strong>{" "}
          reichen rund{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.bedarf} Ausdrucke
          </strong>
          . Passende Pakete mit Drucker beginnen bei{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.min)} €
          </strong>
          , die Hälfte liegt unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.median)} €
          </strong>{" "}
          — etwa {euroDezimal(analyse.proGast)} € pro Gast.
        </p>
        {digital && (
          <p className="mt-4">
            Für eine kleine Feier geht es deutlich günstiger: Ohne Drucker
            starten die Pakete bei{" "}
            <strong className="font-semibold text-slate-900">
              {euro(digital.min)} €
            </strong>
            . Die Gäste bekommen ihre Bilder dann per QR-Code aufs Handy statt
            als Streifen in die Hand. Ob sich das lohnt, hängt weniger vom
            Budget ab als von der Feier: Auf einer Party, bei der ohnehin alle
            am Handy sind, vermisst den Ausdruck kaum jemand. Bei einem runden
            Geburtstag mit mehreren Generationen ist er der Grund, warum die
            Box überhaupt genutzt wird.
          </p>
        )}
        <p className="mt-4">
          <Link
            href="/fotobox-mieten-unter-100-eur"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Fotoboxen unter 100 €
          </Link>{" "}
          im Überblick — alle ohne Drucker.
        </p>
      </Abschnitt>

      <Abschnitt
        id="zuhause"
        titel="Fotobox in der Wohnung — geht das?"
        untertitel="Der Unterschied zwischen Party-Location und Wohnzimmer."
      >
        <p>
          Ja, und es ist der häufigste Fall bei Geburtstagen. Drei Dinge solltet
          ihr vorher prüfen:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">Platz:</strong> Etwa
            zwei mal zwei Meter freie Fläche vor der Box, damit auch drei
            Personen zusammen ins Bild passen. Ein Stativ braucht selbst kaum
            Platz.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">Deckenhöhe:</strong>{" "}
            Bei niedrigen Altbaudecken kann ein Hintergrundsystem zu hoch sein.
            Eine freie Wand tut es auch — hell und einfarbig ist besser als
            gemustert.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">Strom:</strong> Eine
            normale Steckdose reicht. Kamera, Licht und Drucker hängen an einer
            Mehrfachsteckdose, meist liegt ein Verlängerungskabel bei.
          </li>
        </ul>
        <p className="mt-4">
          Für eine Feier zu Hause ist die Versandbox der einfachere Weg: Sie
          kommt ein paar Tage vorher, ihr baut sie in Ruhe auf, und niemand muss
          eine fremde Person in die Wohnung lassen.
        </p>
      </Abschnitt>

      <Abschnitt
        id="runde-geburtstage"
        titel="Runder Geburtstag: 30, 40, 50 oder 60"
        untertitel="Wenn Gäste aus drei Generationen vor der Box stehen."
      >
        <p>
          Bei runden Geburtstagen kommen Kollegen, Nachbarn und Verwandte
          zusammen — und die Hälfte davon hat noch nie eine Fotobox bedient.
          Zwei Dinge helfen mehr als jede Ausstattung:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>
            <strong className="font-semibold text-slate-900">
              Live-Vorschau auf dem Bildschirm.
            </strong>{" "}
            Wer sich vor dem Auslösen sieht, versucht es nicht dreimal.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">
              Ein Drucklayout mit der Zahl.
            </strong>{" "}
            „50" und das Datum auf jedem Streifen macht aus dem Ausdruck ein
            Mitbringsel. {analyse.mitEigenemLayout} der passenden Fotoboxen
            erlauben ein eigenes Layout.
          </li>
        </ul>
      </Abschnitt>

      <Abschnitt
        id="kindergeburtstag"
        titel="Fotobox zum Kindergeburtstag"
        untertitel="Funktioniert — mit zwei Einschränkungen."
      >
        <p>
          Kinder nutzen eine Fotobox intensiver als jede andere Gästegruppe. Das
          ist der Reiz und zugleich das Problem: Ein festes Druckkontingent ist
          bei zehn Kindern in zwei Stunden aufgebraucht. Wer sich das nicht
          ausrechnen will, nimmt eine{" "}
          <Link
            href="/fotobox-mieten-mit-sofortdruck"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Druck-Flatrate
          </Link>{" "}
          — {analyse.mitFlatrate} der passenden Boxen bieten eine an.
        </p>
        <p className="mt-4">
          Zweitens die Höhe: Die meisten Boxen stehen auf einem Stativ und sind
          auf Erwachsene eingestellt. Ein Hocker davor löst das — oder ihr
          stellt das Stativ vor der Feier einmal niedriger.
        </p>
      </Abschnitt>

      <p className="mx-auto max-w-3xl text-left text-xs text-slate-500">
        Zahlen aus den {analyse.anzahlBoxen} Fotoboxen im Vergleich, Stand{" "}
        {stand}.
      </p>
    </>
  );
}
